import { useState, useCallback, useEffect } from 'react';
import { api, type Task } from '../api/client';
import { usePolling } from '../hooks/usePolling';

const PRIORITY_EMOJI: Record<string, string> = { urgent: '🔴', high: '🟠', medium: '🟡', normal: '🟡', low: '🟢' };
const FILTERS = ['pending', 'all', 'done'] as const;

function isOverdue(dueAt: number) { return dueAt < Date.now() / 1000; }

export default function TasksScreen() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'done'>('pending');
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setTasks(await api.tasks(filter));
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  usePolling(load);

  const markDone = async (id: string) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, status: 'done' } : t));
    await api.updateTask(id, { status: 'done' });
  };

  const changePriority = async (id: string, priority: string) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, priority: priority as Task['priority'] } : t));
    await api.updateTask(id, { priority });
  };

  const addTask = async () => {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      await api.createTask({ title: newTitle.trim() });
      setNewTitle('');
      await load();
    } finally { setAdding(false); }
  };

  return (
    <div className="main-content">
      <h1 style={{ fontSize: 22, fontWeight: 800 }}>✅ Tasks</h1>

      {/* Quick Add */}
      <div className="card" style={{ display: 'flex', gap: 10, padding: 12 }}>
        <input
          className="form-input"
          style={{ flex: 1 }}
          placeholder="Add a task…"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addTask()}
        />
        <button className="btn-primary" style={{ width: 'auto', padding: '0 20px' }} onClick={addTask} disabled={adding}>
          {adding ? '…' : '+ Add'}
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="tab-strip">
        {FILTERS.map((f) => (
          <button key={f} className={`tab-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <p style={{ color: 'var(--text-2)' }}>Loading…</p>
        ) : tasks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-emoji">{filter === 'done' ? '🎉' : '✨'}</div>
            <p className="empty-state-text">{filter === 'done' ? 'Nothing completed yet' : 'No tasks — nice!'}</p>
          </div>
        ) : (
          tasks.map((task) => {
            const done = task.status === 'done';
            const overdue = task.dueAt ? isOverdue(task.dueAt) : false;
            return (
              <div key={task.id} className="task-row">
                {/* Checkbox */}
                <button
                  className={`checkbox ${done ? 'done' : ''}`}
                  onClick={() => !done && markDone(task.id)}
                  title={done ? 'Done' : 'Mark done'}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={`task-title ${done ? 'done' : ''}`}>{task.title}</div>
                  {task.ai_summary && <span className="ai-pill">✨ {task.ai_summary}</span>}
                  <div className="task-meta">
                    {/* Priority selector */}
                    <select
                      className="badge"
                      style={{
                        border: 'none', cursor: 'pointer', background: 'var(--surface-raised)',
                        color: 'var(--text-1)', fontFamily: 'inherit', fontSize: 11,
                      }}
                      value={task.priority}
                      onChange={(e) => changePriority(task.id, e.target.value)}
                    >
                      {['urgent', 'high', 'medium', 'low'].map((p) => (
                        <option key={p} value={p}>{PRIORITY_EMOJI[p]} {p}</option>
                      ))}
                    </select>
                    {task.dueAt && (
                      <span className={`task-due ${overdue && !done ? 'overdue' : ''}`}>
                        {overdue && !done ? '⚠ ' : ''}{new Date(task.dueAt * 1000).toLocaleDateString()}
                      </span>
                    )}
                    <span className="task-source">{task.source}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
